/*
  SD rewards for the cart drawer (2026-10-09)

  The drawer section is re-rendered by the theme on every cart change, so these elements reconnect each time with the
  new state already printed by Liquid. This script only adds what Liquid cannot do:
  - sd-cart-ladder: animates the fill from the last state, celebrates a newly unlocked step, warns when a step is lost,
    and writes or clears the two samples cart attributes (SK's samples at step 1, the OGBLEND sample at its step).
    12 Oct 2026: it also keeps the Rs 0 free samples product line in the cart, quantity = samples earned, with a
    "Samples to pack" line property, so invoices list the samples; checkout waits for that update to finish.
  - sd-cart-row: keeps the "Unlocks ..." badge right when a size changes, moves unlocking cards to the front and
    reports a failed add.
  - sd-cart-box: the arrow on the wooden box card opens and closes every box option; reports a failed add.
  The last state lives in sessionStorage, so a step unlocked from a product page still celebrates when the drawer opens.
*/
(() => {
  if (window.customElements.get('sd-cart-ladder')) return;

  // v2 since the ladder went to five steps (10 Oct 2026), so a step number saved under the old order never misfires
  const STORE_KEY = 'sd-ladder-v2';
  const SAMPLE_PROP = 'Samples to pack';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const cartRoot = () => window.Shopify?.routes?.root || '/';

  // The free samples line update in flight, if any; checkout waits for it
  let samplePending = null;

  const readState = () => {
    try {
      return JSON.parse(window.sessionStorage.getItem(STORE_KEY));
    } catch (error) {
      return null;
    }
  };

  const writeState = (state) => {
    try {
      window.sessionStorage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (error) {
      /* storage blocked: the ladder still renders, it just does not animate */
    }
  };

  const formatMoney = (cents) => {
    const format = window.themeVariables?.settings?.moneyFormat || 'Rs. {{amount}}';
    const value = Number(cents) / 100;
    const amount = value.toLocaleString('en-US', {
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    });
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  };

  /* ---------- Pop-ups ---------- */

  let toastTimer;
  const toast = (text, kind = 'unlock') => {
    document.querySelectorAll('.sd-toast').forEach((node) => node.remove());
    const node = document.createElement('div');
    node.className = `sd-toast sd-toast--${kind}`;
    node.setAttribute('role', 'status');
    node.innerHTML = `<span class="sd-toast__icon" aria-hidden="true">${
      kind === 'drop'
        ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M12 7v6M12 17h.01"/></svg>'
        : '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>'
    }</span><span></span>`;
    node.lastElementChild.textContent = text;
    document.body.appendChild(node);
    requestAnimationFrame(() => requestAnimationFrame(() => node.classList.add('is-in')));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      node.classList.remove('is-in');
      setTimeout(() => node.remove(), 400);
    }, kind === 'drop' ? 4200 : 3000);
  };

  const confetti = (origin) => {
    if (reduceMotion || !origin || typeof Element.prototype.animate !== 'function') return;
    const box = origin.getBoundingClientRect();
    const x = box.left + box.width / 2;
    const y = box.top + box.height / 2;
    const colours = ['#d2a24c', '#00b7ff', '#7b3fe4', '#efe7dc', '#f0c417'];

    for (let i = 0; i < 46; i++) {
      const piece = document.createElement('span');
      piece.className = 'sd-confetti';
      piece.style.background = colours[i % colours.length];
      if (i % 3 === 0) piece.style.borderRadius = '50%';
      document.body.appendChild(piece);

      const angle = (Math.PI * 2 * i) / 46 + Math.random() * 0.4;
      const speed = 70 + Math.random() * 120;
      const dx = Math.cos(angle) * speed;
      const dy = Math.sin(angle) * speed - 60;
      const spin = (Math.random() * 2 - 1) * 540;

      piece
        .animate(
          [
            { transform: `translate(${x}px, ${y}px) rotate(0deg) scale(1)`, opacity: 1 },
            { transform: `translate(${x + dx}px, ${y + dy}px) rotate(${spin / 2}deg) scale(1)`, opacity: 1, offset: 0.45 },
            { transform: `translate(${x + dx * 1.2}px, ${y + dy + 160}px) rotate(${spin}deg) scale(.6)`, opacity: 0 },
          ],
          { duration: 1300 + Math.random() * 500, easing: 'cubic-bezier(.2,.7,.3,1)' }
        )
        .finished.then(() => piece.remove(), () => piece.remove());
    }
  };

  /* ---------- Ladder ---------- */

  class SdCartLadder extends HTMLElement {
    connectedCallback() {
      const tier = Number(this.dataset.tier) || 0;
      const fill = Number(this.dataset.fill) || 0;
      const subtotal = Number(this.dataset.subtotal) || 0;
      const previous = readState();

      this.animateFill(previous, fill);

      if (previous && tier !== previous.tier) {
        // Wait one frame so the drawer has finished opening before measuring the node for confetti
        requestAnimationFrame(() => {
          if (!this.isConnected || !this.isVisible()) return;
          if (tier > previous.tier) this.celebrate(tier);
          else if (subtotal > 0) this.warnDrop(previous.tier, subtotal);
        });
      }

      writeState({ tier, fill, subtotal });
      this.syncAttributes(tier);
      this.syncSampleLine();
    }

    isVisible() {
      const drawer = this.closest('cart-drawer');
      return !drawer || drawer.hasAttribute('open');
    }

    animateFill(previous, fill) {
      const bar = this.querySelector('[data-fill-bar]');
      if (!bar || reduceMotion || !previous || previous.fill === fill) return;
      bar.style.transition = 'none';
      bar.style.setProperty('--sd-fill', `${previous.fill}%`);
      bar.getBoundingClientRect();
      bar.style.transition = '';
      requestAnimationFrame(() => bar.style.setProperty('--sd-fill', `${fill}%`));
    }

    celebrate(tier) {
      const node = this.querySelector(`[data-node="${tier}"]`);
      if (!node) return;
      node.classList.add('is-popping');
      confetti(node.querySelector('.sd-ladder__dot'));
      toast((this.dataset.unlockText || 'Unlocked: [reward]').replace('[reward]', node.dataset.reward || ''));
      if (navigator.vibrate && window.matchMedia('(pointer: coarse)').matches) navigator.vibrate(18);
    }

    warnDrop(previousTier, subtotal) {
      const node = this.querySelector(`[data-node="${previousTier}"]`);
      if (!node) return;
      const threshold = Number(node.dataset.threshold) || 0;
      const text = (this.dataset.dropText || '')
        .replace('[threshold]', formatMoney(threshold))
        .replace('[amount]', formatMoney(Math.max(threshold - subtotal, 0)))
        .replace('[reward]', node.dataset.reward || '');
      if (text) toast(text, 'drop');
    }

    // data-attr* is SK's samples (step 1), data-attr2* the OGBLEND sample; both go in one cart update when they change
    syncAttributes(tier) {
      const attributes = {};
      ['attr', 'attr2'].forEach((key) => {
        const name = this.dataset[key];
        if (!name) return;
        const step = Number(this.dataset[`${key}Step`]) || 1;
        const wanted = tier >= step ? this.dataset[`${key}Value`] || 'Yes' : '';
        if (wanted === (this.dataset[`${key}Current`] || '')) return;
        this.dataset[`${key}Current`] = wanted;
        attributes[name] = wanted;
      });
      if (Object.keys(attributes).length === 0) return;
      fetch(`${cartRoot()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
        keepalive: true,
      }).catch(() => {});
    }

    // The Rs 0 free samples line: quantity = samples earned, "Samples to pack" property = the same number, gone at 0.
    // On success the theme re-renders the drawer, this element reconnects with the new state and finds nothing to do.
    syncSampleLine() {
      const variant = Number(this.dataset.sampleVariant) || 0;
      if (!variant || samplePending) return;
      const want = Math.max(0, Number(this.dataset.sampleWant) || 0);
      const have = Number(this.dataset.sampleQty) || 0;
      const key = this.dataset.sampleKey || '';
      if (want === have && (want === 0 || this.dataset.sampleProp === String(want))) return;

      const post = (path, payload) =>
        fetch(`${cartRoot()}${path}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        }).then((response) => (response.ok ? response.json() : Promise.reject(response)));

      let request;
      if (have > 0 && key) {
        const payload = { id: key, quantity: want };
        if (want > 0) payload.properties = { [SAMPLE_PROP]: String(want) };
        request = post('cart/change.js', payload);
      } else if (want > 0) {
        request = post('cart/add.js', { items: [{ id: variant, quantity: want, properties: { [SAMPLE_PROP]: String(want) } }] });
      } else {
        return;
      }

      samplePending = request
        .then(() => {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch(() => {})
        .finally(() => {
          samplePending = null;
        });
    }
  }

  // Checkout waits for a free samples update still in flight, so the order always carries the right line
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('button[name="checkout"], input[name="checkout"]');
      if (!button || !samplePending || !button.form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      samplePending.finally(() => button.form.requestSubmit ? button.form.requestSubmit(button) : button.form.submit());
    },
    true
  );

  /* ---------- Rows ---------- */

  class SdCartRow extends HTMLElement {
    connectedCallback() {
      this.remaining = Number(this.dataset.remaining) || 0;
      this.cards = Array.from(this.querySelectorAll('.sd-card'));
      this.cards.forEach((card) => this.refreshBadge(card));
      this.refreshAllUnlock();
      this.bringUnlockingForward();

      this.addEventListener('change', (event) => {
        if (event.target.matches('select[name="id"]')) this.onSizeChange(event.target);
      });
      this.addEventListener('cart:error', (event) => {
        toast(event.detail?.error || 'This size just sold out.', 'drop');
      });
    }

    onSizeChange(select) {
      const card = select.closest('.sd-card');
      const option = select.selectedOptions[0];
      if (!card || !option) return;
      card.dataset.price = option.dataset.price;
      const price = card.querySelector('[data-price-label]');
      const compare = card.querySelector('[data-compare-label]');
      if (price) price.textContent = option.dataset.priceLabel;
      if (compare) {
        compare.textContent = option.dataset.compareLabel || '';
        compare.hidden = !option.dataset.compareLabel;
      }
      this.refreshBadge(card);
      this.refreshAllUnlock();
    }

    refreshAllUnlock() {
      const all = this.remaining > 0 && this.cards.every((card) => card.classList.contains('is-unlocking'));
      this.classList.toggle('is-all-unlock', all);
      const line = this.querySelector('[data-all-unlock]');
      if (line) line.hidden = !all;
    }

    refreshBadge(card) {
      const unlocks = this.remaining > 0 && Number(card.dataset.price) >= this.remaining;
      const badge = card.querySelector('.sd-card__badge');
      if (badge) badge.hidden = !unlocks;
      card.classList.toggle('is-unlocking', unlocks);
    }

    // Stable: unlocking cards keep their collection order among themselves, and nothing moves when every card unlocks
    bringUnlockingForward() {
      const list = this.querySelector('[data-list]');
      if (!list) return;
      const unlocking = this.cards.filter((card) => card.classList.contains('is-unlocking'));
      if (unlocking.length === 0 || unlocking.length === this.cards.length) return;
      const rest = this.cards.filter((card) => !card.classList.contains('is-unlocking'));
      list.replaceChildren(...unlocking, ...rest);
    }
  }

  /* ---------- Wooden box card ---------- */

  class SdCartBox extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-box-toggle]');
      this.panel = this.querySelector('[data-box-all]');
      if (this.toggle && this.panel) this.toggle.addEventListener('click', () => this.setOpen(this.panel.hidden));
      this.addEventListener('cart:error', (event) => {
        toast(event.detail?.error || 'This box just sold out.', 'drop');
      });
    }

    setOpen(open) {
      this.panel.hidden = !open;
      this.toggle.setAttribute('aria-expanded', String(open));
      this.classList.toggle('is-open', open);
    }
  }

  window.customElements.define('sd-cart-ladder', SdCartLadder);
  window.customElements.define('sd-cart-row', SdCartRow);
  window.customElements.define('sd-cart-box', SdCartBox);

  /* ---------- Savings bump ---------- */

  const bumpSavings = () => {
    const node = document.querySelector('.cart-drawer [data-sd-savings]');
    let last = 0;
    try {
      last = Number(window.sessionStorage.getItem('sd-savings-v1')) || 0;
    } catch (error) {
      /* ignore */
    }
    const now = node ? Number(node.dataset.sdSavings) || 0 : 0;
    if (node && now > last && !reduceMotion) {
      node.classList.remove('is-bump');
      void node.offsetWidth;
      node.classList.add('is-bump');
    }
    try {
      window.sessionStorage.setItem('sd-savings-v1', String(now));
    } catch (error) {
      /* ignore */
    }
  };

  // The theme swaps the drawer footer right after the cart:change event; wait for that before reading the savings line
  document.addEventListener('cart:change', () => setTimeout(bumpSavings, 60));
  bumpSavings();
})();